"use client";

import {
  Card,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Form, useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import IContact from "@/interfaces/contact.interface";
import { ContactFiltersMessages } from "@/messages/contacts/contact-filters.messages";
import { FilterType, FilterValue, IFilter } from "@/types/filter.types";

import { ContactFilterKey } from "../../../ContactFormPage/types";

import { initialFilterValues } from "../../constants";
import { CONSENT_FILTER_VALUES, ConsentFilterValue } from "../../types";

const GRID_SIZES = {
  LARGE: { lg: 3, md: 4, sm: 6, xs: 12 },
  SMALL: { lg: 2, md: 4, sm: 6, xs: 12 },
} as const;

const STATUS_VALUES = {
  TRUE: "true",
  FALSE: "false",
  EMPTY: "",
} as const;

const ARRAY_POSITIONS = {
  FIRST: 0,
  SECOND: 1,
} as const;

const SEARCH_TERMS = {
  SINGLE_WORD: 1,
} as const;

const CONTACT_PROPERTIES = {
  NAME: {
    FIRST: "firstName",
    LAST: "lastName",
    ALL: ["firstName", "lastName"] as const,
  },
  PHONE: {
    MOBILE: "mobilePhone",
    DDI: "ddiMobile",
    DDD: "dddMobile",
    ALL: ["mobilePhone", "ddiMobile", "dddMobile"] as const,
  },
  EMAIL: {
    ADDRESS: "email",
    ALL: ["email"] as const,
  },
} as const;

type Props = {
  messages: ContactFiltersMessages;
};

type FilterState = {
  nameInputValue: string;
  phoneInputValue: string;
  cityInputValue: string;
  stateInputValue: string;
};

interface FilterStrategy {
  canHandle(property: ContactFilterKey): boolean;
  createFilters(value: string): IFilter<IContact>[];
  updateLocalState(
    value: string,
    setState: (updater: (prev: FilterState) => FilterState) => void
  ): void;
  getRelatedProperties(): readonly string[];
}

class FilterValidator {
  static isValueEmpty(value: string | number | null): boolean {
    return value === null || value === "" || (typeof value === "number" && isNaN(value));
  }
}

class FilterStateSynchronizer {
  static syncLocalWithGlobal(
    values: IFilter<IContact>[],
    setState: (updater: (prev: FilterState) => FilterState) => void
  ): void {
    const hasNameFilters = values.some((filter) =>
      CONTACT_PROPERTIES.NAME.ALL.includes(filter.property as "firstName" | "lastName")
    );
    const hasPhoneFilters = values.some((filter) =>
      CONTACT_PROPERTIES.PHONE.ALL.includes(
        filter.property as "mobilePhone" | "ddiMobile" | "dddMobile"
      )
    );

    setState((prevState) => ({
      ...prevState,
      nameInputValue: hasNameFilters ? prevState.nameInputValue : "",
      phoneInputValue: hasPhoneFilters ? prevState.phoneInputValue : "",
    }));
  }
}

class NameFilterStrategy implements FilterStrategy {
  canHandle(property: ContactFilterKey): boolean {
    return property === CONTACT_PROPERTIES.NAME.FIRST;
  }

  createFilters(value: string): IFilter<IContact>[] {
    const searchValue = value.trim();
    if (!searchValue) return [];

    const searchTerms = searchValue.split(" ").filter(Boolean);

    if (searchTerms.length === SEARCH_TERMS.SINGLE_WORD) {
      return [
        {
          property: CONTACT_PROPERTIES.NAME.FIRST,
          filterType: FilterType.Contains,
          value: searchValue,
        },
      ];
    }

    const firstName = searchTerms[ARRAY_POSITIONS.FIRST];
    const lastName = searchTerms.slice(ARRAY_POSITIONS.SECOND).join(" ");

    return [
      {
        property: CONTACT_PROPERTIES.NAME.FIRST,
        filterType: FilterType.Contains,
        value: firstName,
      },
      {
        property: CONTACT_PROPERTIES.NAME.LAST,
        filterType: FilterType.Contains,
        value: lastName,
      },
    ];
  }

  updateLocalState(): void {}

  getRelatedProperties(): readonly string[] {
    return CONTACT_PROPERTIES.NAME.ALL;
  }
}

class PhoneFilterStrategy implements FilterStrategy {
  canHandle(property: ContactFilterKey): boolean {
    return property === CONTACT_PROPERTIES.PHONE.MOBILE;
  }

  createFilters(value: string): IFilter<IContact>[] {
    if (!value || value.replace(/[+\s()_-]/g, "").length === 0) {
      return [];
    }

    const numericValue = removeNonIntegersFromString(value);

    if (numericValue.length === 0) return [];

    return this.createProgressivePhoneFilters(numericValue);
  }

  private createProgressivePhoneFilters(numericValue: string): IFilter<IContact>[] {
    const filters: IFilter<IContact>[] = [];

    if (numericValue.length === 1) {
      const phoneProperties: Array<keyof IContact> = [...CONTACT_PROPERTIES.PHONE.ALL];
      const broadFilters = phoneProperties.map((property) => ({
        property,
        value: numericValue,
        filterType: FilterType.Contains,
      }));
      return broadFilters;
    }

    if (numericValue.length === 2) {
      filters.push({
        property: CONTACT_PROPERTIES.PHONE.DDI,
        value: numericValue,
        filterType: FilterType.Equals,
      });
    } else if (numericValue.length === 3) {
      const ddi = numericValue.slice(0, 2);
      filters.push({
        property: CONTACT_PROPERTIES.PHONE.DDI,
        value: ddi,
        filterType: FilterType.Equals,
      });
    } else if (numericValue.length === 4) {
      const ddi = numericValue.slice(0, 2);
      const ddd = numericValue.slice(2, 4);
      filters.push(
        {
          property: CONTACT_PROPERTIES.PHONE.DDI,
          value: ddi,
          filterType: FilterType.Equals,
        },
        {
          property: CONTACT_PROPERTIES.PHONE.DDD,
          value: ddd,
          filterType: FilterType.Equals,
        }
      );
    } else if (numericValue.length >= 5) {
      const ddi = numericValue.slice(0, 2);
      const ddd = numericValue.slice(2, 4);
      const phoneNumber = numericValue.slice(4);
      filters.push(
        {
          property: CONTACT_PROPERTIES.PHONE.DDI,
          value: ddi,
          filterType: FilterType.Equals,
        },
        {
          property: CONTACT_PROPERTIES.PHONE.DDD,
          value: ddd,
          filterType: FilterType.Equals,
        },
        {
          property: CONTACT_PROPERTIES.PHONE.MOBILE,
          value: phoneNumber,
          filterType: FilterType.Contains,
        }
      );
    }

    return filters;
  }

  updateLocalState(): void {}

  getRelatedProperties(): readonly string[] {
    return CONTACT_PROPERTIES.PHONE.ALL;
  }
}

class EmailFilterStrategy implements FilterStrategy {
  canHandle(property: ContactFilterKey): boolean {
    return property === CONTACT_PROPERTIES.EMAIL.ADDRESS;
  }

  createFilters(value: string): IFilter<IContact>[] {
    const sanitizedValue = this.sanitizeEmail(value);
    if (!sanitizedValue) return [];

    return [
      {
        property: CONTACT_PROPERTIES.EMAIL.ADDRESS,
        filterType: FilterType.Contains,
        value: sanitizedValue,
      },
    ];
  }

  private sanitizeEmail(email: string): string {
    return email.trim().replace(/\s+/g, "");
  }

  updateLocalState(): void {}

  getRelatedProperties(): readonly string[] {
    return CONTACT_PROPERTIES.EMAIL.ALL;
  }
}

class FilterStrategyFactory {
  private strategies: FilterStrategy[] = [
    new NameFilterStrategy(),
    new PhoneFilterStrategy(),
    new EmailFilterStrategy(),
  ];

  getStrategy(property: ContactFilterKey): FilterStrategy | null {
    return this.strategies.find((strategy) => strategy.canHandle(property)) || null;
  }

  getDefaultStrategy(): FilterStrategy {
    return {
      canHandle: () => false,
      createFilters: (value: string) => [
        {
          property: "unknown" as keyof IContact,
          filterType: FilterType.Contains,
          value: value as FilterValue,
        },
      ],
      updateLocalState: () => {},
      getRelatedProperties: () => [],
    };
  }
}

class FilterStateManager {
  constructor(
    private setState: (updater: (prev: FilterState) => FilterState) => void,
    private setValues: (updater: (prev: IFilter<IContact>[]) => IFilter<IContact>[]) => void,
    private values: IFilter<IContact>[],
    private strategyFactory: FilterStrategyFactory = new FilterStrategyFactory()
  ) {}

  updateState(property: ContactFilterKey, value: string | number | null): void {
    if (FilterValidator.isValueEmpty(value)) {
      this.clearFiltersForProperty(property);
      return;
    }

    const strategy = this.getStrategy(property);
    if (!strategy) return;

    const filters = strategy.createFilters(String(value));
    this.updateFilters(property, filters);
  }

  private getStrategy(property: ContactFilterKey): FilterStrategy | null {
    return this.strategyFactory.getStrategy(property);
  }

  private clearFiltersForProperty(property: ContactFilterKey): void {
    const strategy = this.getStrategy(property);
    if (!strategy) return;

    const relatedProperties = strategy.getRelatedProperties();

    this.setValues((prevFilters) =>
      prevFilters.filter((filter) => !relatedProperties.includes(filter.property))
    );
  }

  private updateFilters(property: ContactFilterKey, newFilters: IFilter<IContact>[]): void {
    const strategy = this.getStrategy(property);
    if (!strategy) return;

    const relatedProperties = strategy.getRelatedProperties();

    this.setValues((prevFilters) => {
      const otherFilters = prevFilters.filter(
        (filter) => !relatedProperties.includes(filter.property)
      );
      return [...otherFilters, ...newFilters];
    });
  }

  clearAllFilters(): void {
    this.setValues(() => []);
    this.setState(() => ({
      nameInputValue: "",
      phoneInputValue: "",
      cityInputValue: "",
      stateInputValue: "",
    }));
  }

  syncLocalStateWithFilters(): void {
    FilterStateSynchronizer.syncLocalWithGlobal(this.values, this.setState);
  }
}

const formatPhoneInput = (newValue: string): string => {
  const mask = "+__ (__) _____-____";
  const numbersOnly = newValue.replace(/[^\d]/g, "");

  const limitedNumbers = numbersOnly.slice(0, 13);

  if (limitedNumbers.length === 0) {
    return "";
  }

  let formatted = mask;
  let numberIndex = 0;

  formatted = formatted.replace(/_/g, () => {
    if (numberIndex < limitedNumbers.length) {
      return limitedNumbers[numberIndex++];
    }
    return "_";
  });

  return formatted;
};

function buildAddressStateFilters(rawValue: string): IFilter<IContact>[] {
  const trimmed = rawValue.trim();
  if (!trimmed) return [];

  const upper = trimmed.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) {
    return [
      {
        property: "addressStateUf",
        filterType: FilterType.Equals,
        value: upper,
      },
    ];
  }

  return [
    {
      property: "addressState",
      filterType: FilterType.Contains,
      value: trimmed,
    },
  ];
}

function ContactFiltersForm({ messages }: Props) {
  const { values, setValues, submitForm } = useFormikContext<IFilter<IContact>[]>();
  const [isExpanded, setIsExpanded] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>({
    nameInputValue: "",
    phoneInputValue: "",
    cityInputValue: "",
    stateInputValue: "",
  });

  const filterStateManager = useMemo(
    () => new FilterStateManager(setFilterState, setValues, values),
    [setFilterState, setValues, values]
  );

  const syncLocalStateWithFilters = useCallback(() => {
    filterStateManager.syncLocalStateWithFilters();
  }, [filterStateManager]);

  useEffect(() => {
    syncLocalStateWithFilters();
  }, [syncLocalStateWithFilters]);

  const handleFieldChange = (property: ContactFilterKey, value: string | number | null) => {
    filterStateManager.updateState(property, value);
  };

  const upsertSimpleFilter = (
    property: keyof IContact,
    value: string | boolean | null | undefined,
    filterType: FilterType
  ) => {
    setValues((prevFilters) => {
      const withoutProperty = prevFilters.filter((f) => f.property !== property);
      const isEmpty =
        value === undefined || value === "" || (typeof value === "string" && value.trim() === "");
      if (isEmpty) return withoutProperty;
      return [
        ...withoutProperty,
        { property, filterType, value: value as IFilter<IContact>["value"] },
      ];
    });
  };

  const upsertAddressStateFilter = (rawValue: string) => {
    setFilterState((prev) => ({ ...prev, stateInputValue: rawValue }));
    setValues((prevFilters) => {
      const withoutState = prevFilters.filter(
        (f) => f.property !== "addressState" && f.property !== "addressStateUf"
      );
      return [...withoutState, ...buildAddressStateFilters(rawValue)];
    });
  };

  const compileSubmitFilters = (): IFilter<IContact>[] => {
    const compiled: IFilter<IContact>[] = [];

    const nameStrategy = new NameFilterStrategy();
    compiled.push(...nameStrategy.createFilters(filterState.nameInputValue));

    const emailFilter = values.find((f) => f.property === "email");
    const emailValue = String(emailFilter?.value ?? "").trim();
    if (emailValue) {
      compiled.push({
        property: "email",
        filterType: FilterType.Contains,
        value: emailValue,
      });
    }

    const phoneStrategy = new PhoneFilterStrategy();
    compiled.push(...phoneStrategy.createFilters(filterState.phoneInputValue));

    const activeFilter = values.find((f) => f.property === "active");
    if (activeFilter && activeFilter.value !== undefined) {
      compiled.push(activeFilter);
    }

    const consentFilter = values.find((f) => f.property === "consentStatus");
    const consentValue = String(consentFilter?.value ?? "").trim();
    if (consentValue) {
      compiled.push({
        property: "consentStatus",
        filterType: FilterType.Equals,
        value: consentValue,
      });
    }

    const cityValue = filterState.cityInputValue.trim();
    if (cityValue) {
      compiled.push({
        property: "addressCity",
        filterType: FilterType.Contains,
        value: cityValue,
      });
    }

    compiled.push(...buildAddressStateFilters(filterState.stateInputValue));

    return compiled;
  };

  const getConsentSelectValue = (): ConsentFilterValue => {
    const consentFilter = values.find((f) => f.property === "consentStatus");
    if (!consentFilter || consentFilter.value === "") return CONSENT_FILTER_VALUES.ALL;
    return String(consentFilter.value) as ConsentFilterValue;
  };

  const handleActiveFilterChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value;
    const statusValue =
      selectedValue === STATUS_VALUES.TRUE
        ? true
        : selectedValue === STATUS_VALUES.FALSE
          ? false
          : undefined;
    upsertSimpleFilter("active", statusValue, FilterType.Equals);
  };

  const handleConsentFilterChange = (event: SelectChangeEvent<ConsentFilterValue>) => {
    const selected = event.target.value;
    if (selected === CONSENT_FILTER_VALUES.ALL) {
      upsertSimpleFilter("consentStatus", undefined, FilterType.Equals);
    } else {
      upsertSimpleFilter("consentStatus", selected, FilterType.Equals);
    }
  };

  const handleClearFilters = async () => {
    filterStateManager.clearAllFilters();
    setFilterState({
      nameInputValue: "",
      phoneInputValue: "",
      cityInputValue: "",
      stateInputValue: "",
    });
    await setValues(initialFilterValues);
    submitForm();
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    const filtersToSubmit = compileSubmitFilters();
    await setValues(filtersToSubmit);
    submitForm();
  };

  const handleToggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const getValueFromProperty = (property: string): IFilter<IContact> | undefined => {
    if (property === CONTACT_PROPERTIES.NAME.FIRST) {
      return {
        property: CONTACT_PROPERTIES.NAME.FIRST,
        filterType: FilterType.Contains,
        value: filterState.nameInputValue,
      } as IFilter<IContact>;
    }

    if (property === CONTACT_PROPERTIES.PHONE.MOBILE) {
      return {
        property: CONTACT_PROPERTIES.PHONE.MOBILE,
        filterType: FilterType.Contains,
        value: "",
      } as IFilter<IContact>;
    }

    return values.find((filter) => filter.property === property);
  };

  return (
    <Card>
      <Form onSubmit={handleSubmit}>
        <MDBox p={2}>
          <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          <Grid size={GRID_SIZES.LARGE} sx={{ minWidth: 0 }}>
            <TextField
              name={CONTACT_PROPERTIES.NAME.FIRST}
              label={messages.firstNameLabel}
              onChange={(e) => {
                const newValue = e.target.value;
                setFilterState((prev) => ({ ...prev, nameInputValue: newValue }));
                handleFieldChange(CONTACT_PROPERTIES.NAME.FIRST, newValue);
              }}
              value={filterState.nameInputValue}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>
          <Grid size={GRID_SIZES.LARGE} sx={{ minWidth: 0 }}>
            <TextField
              name={CONTACT_PROPERTIES.EMAIL.ADDRESS}
              label={messages.emailLabel}
              onChange={(e) => handleFieldChange(CONTACT_PROPERTIES.EMAIL.ADDRESS, e.target.value)}
              value={getValueFromProperty(CONTACT_PROPERTIES.EMAIL.ADDRESS)?.value ?? ""}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>
          <Grid size={GRID_SIZES.LARGE} sx={{ minWidth: 0 }}>
            <TextField
              name={CONTACT_PROPERTIES.PHONE.MOBILE}
              label={messages.mobilePhoneLabel}
              placeholder="+55 (11) 99999-9999"
              value={filterState.phoneInputValue}
              onChange={(e) => {
                const newValue = e.target.value;
                const formatted = formatPhoneInput(newValue);
                setFilterState((prev) => ({ ...prev, phoneInputValue: formatted }));
                handleFieldChange(CONTACT_PROPERTIES.PHONE.MOBILE, formatted);
              }}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>

          <Grid size={GRID_SIZES.LARGE} sx={{ minWidth: 0 }}>
            <FormControl fullWidth>
              <InputLabel shrink>{messages.activeLabel}</InputLabel>
              <Select
                value={String(getValueFromProperty("active")?.value ?? STATUS_VALUES.EMPTY)}
                onChange={handleActiveFilterChange}
                label={messages.activeLabel}
                displayEmpty
                inputProps={{ "aria-label": messages.activeLabel }}
              >
                <MenuItem value={STATUS_VALUES.EMPTY}>{messages.selectStatusPlaceholder}</MenuItem>
                <MenuItem value={STATUS_VALUES.TRUE}>{messages.statusTrueOption}</MenuItem>
                <MenuItem value={STATUS_VALUES.FALSE}>{messages.statusFalseOption}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Collapse in={isExpanded} unmountOnExit>
              <Grid container spacing={2}>
                <Grid size={GRID_SIZES.LARGE} sx={{ minWidth: 0 }}>
                  <TextField
                    name="addressCity"
                    label={messages.cityLabel}
                    value={filterState.cityInputValue}
                    onChange={(e) =>
                      setFilterState((prev) => ({ ...prev, cityInputValue: e.target.value }))
                    }
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={GRID_SIZES.LARGE} sx={{ minWidth: 0 }}>
                  <TextField
                    name="addressState"
                    label={messages.stateLabel}
                    value={filterState.stateInputValue}
                    onChange={(e) => upsertAddressStateFilter(e.target.value)}
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={GRID_SIZES.LARGE} sx={{ minWidth: 0 }}>
                  <FormControl fullWidth>
                    <InputLabel shrink>{messages.consentLabel}</InputLabel>
                    <Select
                      value={getConsentSelectValue()}
                      label={messages.consentLabel}
                      onChange={handleConsentFilterChange}
                      displayEmpty
                      inputProps={{ "aria-label": messages.consentLabel }}
                    >
                      <MenuItem value={CONSENT_FILTER_VALUES.ALL}>
                        {messages.consentAllOption}
                      </MenuItem>
                      <MenuItem value={CONSENT_FILTER_VALUES.GRANTED}>
                        {messages.consentCompletedOption}
                      </MenuItem>
                      <MenuItem value={CONSENT_FILTER_VALUES.PENDING}>
                        {messages.consentPendingOption}
                      </MenuItem>
                      <MenuItem value={CONSENT_FILTER_VALUES.NOT_GRANTED}>
                        {messages.consentNotCompletedOption}
                      </MenuItem>
                      <MenuItem value={CONSENT_FILTER_VALUES.CANCELED}>
                        {messages.consentCanceledOption}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Collapse>

            <Grid container spacing={2} mt={isExpanded ? 2 : 0}>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton
                  type="button"
                  variant="outlined"
                  color="dark"
                  onClick={handleToggleExpanded}
                  fullWidth
                >
                  {isExpanded ? messages.lessFiltersButton : messages.moreFiltersButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton
                  type="button"
                  variant="gradient"
                  color="light"
                  onClick={() => void handleClearFilters()}
                  fullWidth
                >
                  {messages.clearFilterButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton type="submit" variant="gradient" color="dark" fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        </MDBox>
      </Form>
    </Card>
  );
}

export default ContactFiltersForm;
